import { emailService } from './emailService';
import { notificationService } from './notificationService';

class NotificationQueueService {
    constructor() {
        this.queue = [];
        this.processing = false;
        this.maxRetries = 3;
        this.retryDelay = 5000; // 5 seconds
        this.loadQueueFromStorage();
    }

    loadQueueFromStorage() {
        try {
            const savedQueue = localStorage.getItem('notificationQueue');
            if (savedQueue) {
                this.queue = JSON.parse(savedQueue);
                console.log('Loaded queue from storage:', this.queue.length, 'items');
            }
        } catch (error) {
            console.error('Error loading queue from storage:', error);
        }
    }

    saveQueueToStorage() {
        try {
            localStorage.setItem('notificationQueue', JSON.stringify(this.queue));
        } catch (error) {
            console.error('Error saving queue to storage:', error);
        }
    }

    async addToQueue(notification) {
        try {
            this.queue.push({
                ...notification,
                retries: 0,
                status: 'pending',
                timestamp: new Date().toISOString()
            });
            this.saveQueueToStorage();
            console.log('Added notification to queue:', notification.type, 'for user:', notification.userId);
            this.processQueue();
        } catch (error) {
            console.error('Error adding notification to queue:', error);
        }
    }

    async addBulkToQueue(notifications) {
        notifications.forEach(notification => {
            this.queue.push({
                ...notification,
                retries: 0,
                status: 'pending'
            });
        });
        this.processQueue();
    }

    async processQueue() {
        if (this.processing || this.queue.length === 0) return;

        this.processing = true;
        const batchSize = 10;
        const batch = this.queue.splice(0, batchSize);

        try {
            await Promise.all(batch.map(notification => this.processNotification(notification)));
            this.saveQueueToStorage();
        } catch (error) {
            console.error('Error processing notification batch:', error);
            this.queue.unshift(...batch.filter(n => n.status === 'failed'));
            this.saveQueueToStorage();
        } finally {
            this.processing = false;
            if (this.queue.length > 0) {
                setTimeout(() => this.processQueue(), 1000);
            }
        }
    }

    async processNotification(notification) {
        try {
            console.log('Processing notification:', notification.type, 'for user:', notification.userId);
            
            const preferences = await notificationService.getUserPreferences(notification.userId);
            
            if (!preferences[notification.type]?.email) {
                console.log('Notification skipped - user preferences disabled:', notification.type);
                notification.status = 'skipped';
                return;
            }

            await emailService.sendTemplateEmail({
                to: notification.email,
                template: notification.type,
                data: notification.data
            });

            if (preferences[notification.type]?.inApp) {
                await notificationService.sendNotification({
                    type: notification.type,
                    userId: notification.userId,
                    data: notification.data,
                    priority: notification.priority,
                    channels: ['IN_APP']
                });
            }

            notification.status = 'completed';
            console.log('Notification processed successfully:', notification.type);
        } catch (error) {
            console.error(`Error processing notification ${notification.id}:`, error);
            
            if (notification.retries < this.maxRetries) {
                notification.retries++;
                notification.status = 'retrying';
                notification.lastError = error.message;
                this.queue.push(notification);
            } else {
                notification.status = 'failed';
                notification.lastError = error.message;
                this.logFailedNotification(notification);
            }
        }
    }

    logFailedNotification(notification) {
        console.error('Failed notification:', {
            id: notification.id,
            type: notification.type,
            userId: notification.userId,
            email: notification.email,
            data: notification.data,
            error: notification.lastError,
            timestamp: notification.timestamp
        });
    }

    getQueueStatus() {
        return {
            total: this.queue.length,
            pending: this.queue.filter(n => n.status === 'pending').length,
            processing: this.processing,
            failed: this.queue.filter(n => n.status === 'failed').length
        };
    }

    clearQueue() {
        this.queue = [];
        this.processing = false;
    }
}

export const notificationQueueService = new NotificationQueueService(); 